import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Copy, Check, BookOpen, Globe, Library, ShoppingBag } from "lucide-react";
import { useState } from "react";
import { ShareSection } from "../components/ShareSection";

const ISBN = "9781067688004";
const TITLE = "The Detective Diaries of Marchfield Meadows";
const AUTHOR = "Terata B.";

// Encoded search query helpers
const enc = encodeURIComponent;
const ISBN_Q = enc(ISBN);
const TITLE_Q = enc(TITLE);

const RETAILERS = [
  {
    name: "Barnes & Noble",
    region: "USA",
    note: "Largest US bookstore chain",
    url: `https://www.barnesandnoble.com/s/${ISBN_Q}`,
    color: "#2A9D8F"
  },
  {
    name: "Books-A-Million",
    region: "USA",
    note: "Order in-store or online",
    url: `https://www.booksamillion.com/search?query=${ISBN_Q}`,
    color: "#E63946"
  },
  {
    name: "Powell's Books",
    region: "USA",
    note: "Independent bookstore",
    url: `https://www.powells.com/searchresults?keyword=${ISBN_Q}`,
    color: "#457B9D"
  },
  {
    name: "Bookshop.org",
    region: "USA",
    note: "Supports local indie bookstores",
    url: `https://bookshop.org/search?keywords=${ISBN_Q}`,
    color: "#E9C46A"
  },
  {
    name: "Indigo / Chapters",
    region: "Canada",
    note: "Canada's largest book retailer",
    url: `https://www.indigo.ca/en-ca/search?keywords=${ISBN_Q}`,
    color: "#2A9D8F"
  },
  {
    name: "Waterstones",
    region: "UK",
    note: "UK's leading bookseller",
    url: `https://www.waterstones.com/books/search/term/${ISBN_Q}`,
    color: "#E63946"
  },
  {
    name: "Blackwell's",
    region: "UK",
    note: "Free international shipping",
    url: `https://blackwells.co.uk/bookshop/search?keyword=${ISBN_Q}`,
    color: "#457B9D"
  },
  {
    name: "Booktopia",
    region: "Australia",
    note: "Australia's local bookstore",
    url: `https://www.booktopia.com.au/search.ep?keywords=${ISBN_Q}`,
    color: "#E9C46A"
  },
  {
    name: "Wheelers Books",
    region: "New Zealand",
    note: "NZ & Pacific region",
    url: `https://www.wheelers.co.nz/search/?keyword=${ISBN_Q}`,
    color: "#2A9D8F"
  },
  {
    name: "WorldCat (Libraries)",
    region: "Worldwide",
    note: "Find a library that has it",
    url: `https://www.worldcat.org/search?q=${ISBN_Q}`,
    color: "#457B9D"
  }
];

const CopyableField = ({ label, value, testId }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="bg-white rounded-xl p-4 border-3 border-[#1D3557]/10 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-[#1D3557]/60 font-bold uppercase tracking-wider mb-1">{label}</p>
        <p className="font-bold text-[#1D3557] truncate" data-testid={`${testId}-value`}>{value}</p>
      </div>
      <button
        onClick={handleCopy}
        className="flex-shrink-0 px-3 py-2 rounded-lg bg-[#1D3557] text-white font-bold text-sm hover:bg-[#2A9D8F] transition-colors flex items-center gap-1"
        data-testid={`${testId}-copy-btn`}
      >
        {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
      </button>
    </div>
  );
};

const WhereToBuyPage = () => {
  return (
    <div className="min-h-screen bg-[#FDFBF7] py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#1D3557] font-bold mb-8 hover:text-[#E63946] transition-colors"
          data-testid="back-home-link"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="text-5xl mb-4">📚🌍</div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#1D3557] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Where to Buy
          </h1>
          <p className="text-lg text-[#1D3557] font-medium max-w-2xl mx-auto">
            Distributed worldwide through IngramSpark. Ask any bookstore or library to order it — just give them the ISBN below!
          </p>
        </motion.div>

        {/* Book details — copyable */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="tactile-card p-6 mb-12 bg-[#E9C46A]/20"
          data-testid="book-details-card"
        >
          <h2 className="text-xl font-bold text-[#1D3557] mb-4 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            <BookOpen className="w-6 h-6 text-[#E63946]" />
            Book Details — Tap to copy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <CopyableField label="ISBN-13" value={ISBN} testId="isbn" />
            <CopyableField label="Title" value={TITLE} testId="title" />
            <CopyableField label="Author" value={AUTHOR} testId="author" />
          </div>
        </motion.div>

        {/* Amazon callout */}
        <motion.a
          href="https://www.amazon.com/Detective-Diaries-Marchfield-Meadows/dp/1067688005"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          whileHover={{ y: -2 }}
          className="tactile-card p-6 mb-12 border-4 border-[#E63946] bg-[#E63946]/10 block transition-shadow"
          data-testid="amazon-callout"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <ShoppingBag className="w-10 h-10 text-[#E63946] flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-[#1D3557]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Now live on Amazon worldwide
              </h3>
              <p className="text-[#1D3557] font-medium">
                Available on Amazon US, UK, Canada, Australia, Caribbean, Latin America &amp; more — tap here to buy directly.
              </p>
            </div>
            <span className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#E63946] text-white font-bold">
              Buy on Amazon →
            </span>
          </div>
        </motion.a>

        {/* Retailers grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-[#1D3557] mb-6 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            <Globe className="w-7 h-7 text-[#2A9D8F]" />
            Search a Retailer
          </h2>
          <p className="text-[#1D3557]/80 font-medium mb-6">
            Each link runs an ISBN search on that retailer. If results don't appear yet, the title is still rolling out through IngramSpark's distribution network — try again in a few days.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RETAILERS.map((r, idx) => (
              <motion.a
                key={r.name}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + idx * 0.04 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="tactile-card p-5 group transition-shadow"
                data-testid={`retailer-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={{ borderTopWidth: 6, borderTopStyle: "solid", borderTopColor: r.color }}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-[#1D3557] text-lg leading-tight" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                    {r.name}
                  </h3>
                  <ExternalLink className="w-4 h-4 text-[#1D3557]/40 group-hover:text-[#E63946] transition-colors flex-shrink-0 mt-1" />
                </div>
                <span className="inline-block px-2 py-0.5 rounded-full bg-[#1D3557]/10 text-[#1D3557] text-xs font-bold mb-2">
                  {r.region}
                </span>
                <p className="text-sm text-[#1D3557]/70 font-medium">{r.note}</p>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Share / spread the word */}
        <div className="mt-12">
          <ShareSection variant="full" />
        </div>

        {/* Librarian tip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="tactile-card p-6 mt-12 bg-[#2A9D8F]/10"
          data-testid="librarian-tip"
        >
          <div className="flex flex-col md:flex-row md:items-start gap-4">
            <Library className="w-10 h-10 text-[#2A9D8F] flex-shrink-0" />
            <div>
              <h3 className="text-xl font-bold text-[#1D3557] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                For Librarians, Schools &amp; Booksellers
              </h3>
              <p className="text-[#1D3557] font-medium mb-2">
                Order through your usual Ingram Content Group / IngramSpark wholesale account. Standard trade discounts apply.
              </p>
              <p className="text-[#1D3557]/80 font-medium text-sm">
                Questions about bulk orders? Email <a href="mailto:info@farmsmartkids.com" className="text-[#E63946] font-bold underline">info@farmsmartkids.com</a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhereToBuyPage;
